import React from 'react'
import { AccountNotificationService } from '../../../services/account-notification'
import { useService } from '../../../hooks/use-service'
import { OrgNotificationModel } from '../../../models/notification/org-notification.model'
import type { ILoadableDataState } from '../../../hooks/use-loadable-data-ref-state'
import { useLoadableDataRefState } from '../../../hooks/use-loadable-data-ref-state'
import { parseNotificationVariantFromApiResponse } from './parse-org-notification-model'
import { InsightsService } from '../../../services/insights'
import { arrayUnqiue } from '../../../_utils/array'
import { ApiVersion } from '../../../enums/api-version.enum'
import { AppService } from '../../../services'

interface IOrgNotificationLoaderProps {
    orgId: number
    notifId: string | undefined
    stats?: any
    children: (
        notification: React.MutableRefObject<ILoadableDataState<OrgNotificationModel | undefined>>,
    ) => JSX.Element
    includeCancelledChildren?: boolean
}

const OrgNotificationLoader = ({
    orgId,
    notifId,
    stats,
    children,
    includeCancelledChildren,
}: IOrgNotificationLoaderProps) => {
    const appSvc = useService(AppService)
    const svc = useService<AccountNotificationService>(AccountNotificationService)
    const statsSvc = useService<InsightsService>(InsightsService)
    const [loader, setLoader] = useLoadableDataRefState<OrgNotificationModel | undefined>({ loading: true })

    React.useEffect(() => {
        const loadData = async () => {
            setLoader({ ...loader, loading: true })
            const update: any = { loading: false }

            if (notifId) {
                const { ok, data } = await svc.fetchById(orgId, notifId, {
                    query: {
                        include_segments: true,
                    },
                    version: ApiVersion.V4,
                })
                if (ok) {
                    const model = OrgNotificationModel.build(data)
                    const variant = parseNotificationVariantFromApiResponse(data, includeCancelledChildren)

                    const uqOrgSegmentIds = arrayUnqiue(model.getSegmentIds() ?? [])

                    if (uqOrgSegmentIds.length) {
                        variant.getAudience().setSegmentIds(uqOrgSegmentIds)
                    }

                    const uqOrgExcludedSegmentIds = arrayUnqiue(model.getExcludedSegmentIds() ?? [])

                    if (uqOrgExcludedSegmentIds.length) {
                        variant.getAudience().setExcludedSegmentIds(uqOrgExcludedSegmentIds)
                    }

                    model.setVariant(variant)
                    update.data = model

                    if (stats === true || (typeof stats === 'object' && Object.keys(stats).length > 0)) {
                        const domainIds = arrayUnqiue(model.getNotifications().map((n) => n.domainId))
                        const overrides = typeof stats === 'object' ? stats : {}

                        const insightsPkg = {
                            dateIncrement: 'lifetime',
                            datePreset: 'lifetime',
                            fields: ['notification_group.id', 'impressions', 'clicks', 'ctr', 'ctr_decimal'],
                            ...overrides,
                            entity: 'notifications',
                            breakdowns: ['notification_group'],
                            filters: [
                                {
                                    field: 'domain.id',
                                    operator: 'in',
                                    value: domainIds,
                                },
                                {
                                    field: 'notification_group.id',
                                    operator: 'eq',
                                    value: model.getId(),
                                },
                            ],
                        }

                        const statsRes = await statsSvc.fetch(insightsPkg, {
                            requestScope: 'organizations',
                            scopeId: orgId,
                            nestedScope: 'notifications',
                            cancellationKey: 'org-notifs-list-stats',
                        })
                        const statsMatch = statsRes?.find((r) => r.notification_group?.id === model.getId())

                        if (!!statsMatch) {
                            model.setStats({
                                impressions: statsMatch.impressions,
                                clicks: statsMatch.clicks,
                                ctr: statsMatch.ctr,
                                ctr_decimal: statsMatch.ctr_decimal,
                            })
                        }
                    }
                } else {
                    appSvc.routeBack()
                }
            }

            setLoader(update)
        }

        loadData()
    }, [orgId, notifId])

    return children(loader)
}

export default OrgNotificationLoader
